/**
 * Command Center (no ML) — run one geo test measured by a partner.
 * Same steps as Command Center + ML: Plan → Design → Test plan → In flight →
 * Decide. The partner's design deck and its mid-flight results decks are uploaded,
 * stored and read by the backend (deck_parser.py); guardrails come from the
 * tenant's rules, evaluated by the backend.
 */

import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Badge, BudgetInput, CcStyles, ChangeField, ReadoutChart, printElement, ChecksList, ChecksSummary, DecisionField, ErrorBanner, StageBar, StartDateField, UploadIcon, WeeksInput, errMsg,
  useChecks, useSavedTest,
} from "./components";
import { LibraryEntry, StoredDeck, ccApi, ccBase } from "./api";
import { addDays, decisionText, defaultStart, dollars, fmtDayYear, migrateFlow, money, parseDay, todayIso } from "./common";
import { DECK_DEFAULT, DECK_FIELD_LABEL, DECK_STAGES, DeckState, computeDeck, deckResult, deckStatus } from "./deckModel";

const BASE = ccBase("deck");
const DECK_TYPES = ".pdf,.ppt,.pptx,application/pdf";

const fmtSize = (b: number) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

/** Drop zone → real upload with progress → stored file. */
const DeckUpload: React.FC<{
  title: string; kind: "design" | "results"; stored: StoredDeck | null; uploading: { name: string; pct: number } | null;
  onPick: (f: File) => void; onDownload: () => void; doneText: string; hint: string;
}> = ({ title, kind, stored, uploading, onPick, onDownload, doneText, hint }) => {
  const input = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);
  return (
    <div className="cc-card">
      <div className="cc-qh">
        <span>{title}</span>
        <Badge tone={stored ? "green" : uploading ? "blue" : "grey"}>{stored ? doneText : uploading ? `Uploading… ${uploading.pct}%` : "Not uploaded"}</Badge>
      </div>
      <input ref={input} type="file" accept={DECK_TYPES} hidden onChange={e => { const f = e.target.files?.[0]; if (f) onPick(f); e.target.value = ""; }} />
      {!stored && !uploading ? (
        <button className="cc-drop" style={drag ? { borderColor: "var(--blue)" } : undefined} onClick={() => input.current?.click()}
          onDragOver={e => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
          onDrop={e => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f) onPick(f); }}>
          <UploadIcon />
          <span style={{ fontWeight: 600 }}>Drop the {kind} deck here</span>
          <span className="cc-help">PDF or PPTX · or click to browse · {hint}</span>
        </button>
      ) : (
        <>
          <div className="cc-pdf">
            <span className="cc-pdf-ic">{(stored?.name || uploading?.name || "").toLowerCase().endsWith(".pdf") ? "PDF" : "PPT"}</span>
            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <span className="cc-mono" style={{ fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{stored?.name || uploading?.name}</span>
                <span className="cc-help">{stored ? `${fmtSize(stored.size)} · stored` : `${uploading?.pct}%`}</span>
              </div>
              <div className="cc-bar"><span style={{ width: `${stored ? 100 : uploading?.pct || 0}%` }} /></div>
            </div>
            {stored && <button className="cc-btn" style={{ padding: "6px 12px" }} onClick={onDownload}>Download</button>}
            {stored && <button className="cc-btn" style={{ padding: "6px 12px" }} onClick={() => input.current?.click()}>Replace</button>}
          </div>
        </>
      )}
    </div>
  );
};

export default function DeckTestFlow() {
  const { id } = useParams();
  const navigate = useNavigate();
  const t = useSavedTest<DeckState>({ mode: "deck", testId: id, defaults: { ...DECK_DEFAULT, startDate: defaultStart() }, statusOf: deckStatus, migrate: migrateFlow });
  const { state: s, update, engine, loading } = t;
  const d = computeDeck(s, engine);
  const { forStage, blockers } = useChecks(id, t.version);
  const [uploading, setUploading] = useState<Record<string, { name: string; pct: number } | null>>({});
  const [library, setLibrary] = useState<LibraryEntry[]>([]);
  const planRef = useRef<HTMLElement>(null);

  useEffect(() => { ccApi.library("deck").then(setLibrary).catch(() => {}); }, []);

  const go = (n: number) => update(p => ({ stage: n, maxStage: Math.max(p.maxStage, n) }));
  const canCreate = !!(s.name.trim() && s.adv.trim());
  const status = deckStatus(s);

  const onCreate = async () => {
    if (!canCreate) return;
    try {
      const created = await t.create({ ...s, stage: 1, maxStage: 1 });
      navigate(`${BASE}/tests/${created.id}`, { replace: true });
    } catch (err) {
      t.setError(`Could not create the test: ${errMsg(err)}`);
    }
  };

  const uploadDeck = async (kind: "design" | "results", file: File) => {
    if (!id) return;
    setUploading(u => ({ ...u, [kind]: { name: file.name, pct: 0 } }));
    try {
      const test = await ccApi.uploadDeck(id, kind, file, pct => setUploading(u => ({ ...u, [kind]: { name: file.name, pct } })), s.phase >= 2);
      t.applyTest(test);
      // The flight length comes from the design deck.
      const weeks = test.engine?.decks?.design?.parsed?.fields?.weeks;
      update(kind === "design" ? { dUp: 2, ...(weeks ? { flightWeeks: weeks } : {}) } : { rUp: 2 });
    } catch (err) {
      t.setError(`Upload failed: ${errMsg(err)}`);
    } finally {
      setUploading(u => ({ ...u, [kind]: null }));
    }
  };
  const download = (kind: "design" | "results") => {
    const deck = engine.decks?.[kind];
    if (id && deck) ccApi.downloadDeck(id, kind, deck.name).catch(err => t.setError(`Download failed: ${errMsg(err)}`));
  };

  if (loading) return <div className="cc-page"><CcStyles /><div className="cc-skel" style={{ height: 320 }} /></div>;

  const rev = d.rev;
  const title = s.stage === 0 ? "New geo test" : `${d.advName} · ${s.name.trim() || "New geo test"}`;
  const question = decisionText(s.decision) || "Did the test work?";
  const maxWeeks = s.maxWeeks ?? 6;
  const planBlocks = blockers("plan");
  const designBlocks = blockers("design");
  const designStored = !!d.designDeck;
  const resultsStored = !!d.resultsDeck;
  const designReadable = designStored && !d.designParse?.error;
  const resultsReadable = resultsStored && !d.resultsParse?.error;
  const designNextOk = designReadable && !designBlocks.length;
  const missingText = (m?: string[]) => (m || []).map(k => DECK_FIELD_LABEL[k] || k).join(", ");
  const avg = (v: number[]) => (v.length ? v.reduce((a, b) => a + b, 0) / v.length : 0);
  const winTexts: Record<string, string> = {
    scale: `${d.advName} increases the budget for this channel.`, roll: `${d.advName} rolls the campaign out to all markets.`, keep: `${d.advName} keeps current spend, now with proof it works.`,
  };
  const decDefs = d.met
    ? [["scale", "Increase the budget (as agreed in the plan)"], ["keep", "Keep current spend"], ["retest", "Retest in another season or market set"]]
    : [["keep", "Keep spend as is"], ["retest", "Retest with more weeks or budget"], ["stop", "Stop or cut this spend"]];
  const phases: [string, Date | null, string][] = [
    ["Scheduled", d.start ? addDays(d.start, -7) : null, "Plan approved"],
    ["Running", d.start, "Ads live in test markets"],
    ["Ended", d.end, "Ads stop; last results deck is final"],
  ];
  const pill = (on: boolean) => `cc-pill${on ? " on" : ""}`;
  const opt = (on: boolean) => `cc-opt${on ? " on" : ""}`;
  const downloadPlan = () => printElement(planRef.current, `Test plan - ${d.advName} - ${s.name.trim() || "geo test"}`);

  return (
    <div className="cc-page">
      <CcStyles />

      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-end", gap: 12 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div className="cc-crumb"><button onClick={() => navigate(BASE)}>Command Center</button> / {title}</div>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
            <h1 className="cc-h1">{title}</h1>
            <Badge tone={status.tone}>{status.label}</Badge>
            <span className="cc-chip" style={{ color: "var(--violet)", background: "var(--violet-subtle)" }}>Command Center (no ML) · stats by {d.partner}</span>
            {s.stage > 0 && d.method && <span className="cc-chip">{d.method === "RCT" ? `RCT${d.dz.split ? ` ${d.dz.split}/${100 - d.dz.split}` : ""}` : d.method}</span>}
            {s.stage > 0 && <span className="cc-chip">{d.kpiShort}</span>}
          </div>
        </div>
        {t.saving && <span className="cc-help">Saving…</span>}
      </div>

      <ErrorBanner msg={t.error} onClose={() => t.setError(null)} />

      {s.stage > 0 && <StageBar labels={DECK_STAGES} stage={s.stage} maxStage={s.maxStage} onGo={n => update({ stage: n })} />}

      {/* ── 0 · CREATE ── */}
      {s.stage === 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-start" }}>
          <div className="cc-card" style={{ flex: "1 1 560px", minWidth: 0 }}>
            <span style={{ fontWeight: 600, fontSize: 16 }}>Create a new geo test</span>
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span className="cc-fl">Test name</span>
              <input className="cc-input" value={s.name} onChange={e => t.setState(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Q1 CTV launch" />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span className="cc-fl">Advertiser / brand</span>
              <input className="cc-input" value={s.adv} onChange={e => t.setState(p => ({ ...p, adv: e.target.value }))} placeholder="e.g. Acme Home" />
            </label>
            <span className="cc-fl" style={{ marginTop: 4 }}>What does the KPI measure?</span>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className={opt(rev)} onClick={() => t.setState(p => ({ ...p, kpi: "rev" }))}>
                <span style={{ fontWeight: 600 }}>Revenue ($)</span>
                <span className="cc-help">Sales in dollars. Results show lift, incremental $ and iROAS.</span>
              </button>
              <button className={opt(!rev)} onClick={() => t.setState(p => ({ ...p, kpi: "cnt" }))}>
                <span style={{ fontWeight: 600 }}>A count</span>
                <span className="cc-help">Orders, sign-ups, store visits, installs. Results show lift and cost per incremental; no $.</span>
              </button>
            </div>
            <span className="cc-fl" style={{ marginTop: 4 }}>Who runs the maths?</span>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className={opt(s.partner === "adrsta")} onClick={() => t.setState(p => ({ ...p, partner: "adrsta" }))}>
                <span style={{ fontWeight: 600 }}>Adrsta</span>
                <span className="cc-help">Designs the test and sends back a design deck and a results deck.</span>
              </button>
              <button className={opt(s.partner === "other")} onClick={() => t.setState(p => ({ ...p, partner: "other" }))}>
                <span style={{ fontWeight: 600 }}>Another partner</span>
                <span className="cc-help">Any measurement partner that shares a design deck and a results deck.</span>
              </button>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", paddingTop: 4 }}>
              <span className="cc-help">{canCreate ? "Ready to create." : "Enter a test name and advertiser."}</span>
              <button className={`cc-btn${canCreate ? " primary" : ""}`} disabled={!canCreate} onClick={onCreate}>Create test →</button>
            </div>
          </div>
          <div style={{ flex: "1 1 280px", maxWidth: 340, display: "flex", flexDirection: "column", gap: 12 }}>
            <div className="cc-card">
              <span style={{ fontWeight: 600 }}>Who does what?</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: 13, lineHeight: 1.5, color: "var(--text-secondary)" }}>
                <div><b style={{ color: "var(--text-primary)" }}>Command Center</b> keeps the plan, guardrails, test plan, tracking, decision and learning library.</div>
                <div><b style={{ color: "var(--text-primary)" }}>{d.partnerCap}</b> runs the power analysis and the lift measurement, and shares them as decks.</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── 1 · PLAN ── */}
      {s.stage === 1 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 480px", minWidth: 0, display: "flex", flexDirection: "column", gap: 12 }}>
            <DecisionField value={s.decision} onChange={v => update({ decision: v })} />
            <StartDateField value={s.startDate} onChange={v => update({ startDate: v })} />
            <div className="cc-card">
              <div className="cc-qh"><span>Longest the advertiser can run the test</span></div>
              <WeeksInput value={maxWeeks} onChange={w => update({ maxWeeks: w, ...(d.dz.weeks ? {} : { flightWeeks: w }) })} ariaLabel="Longest the advertiser can run the test, in weeks" />
              <span className="cc-help">Sent to {d.partner} as a limit; the design deck sets the flight length.</span>
            </div>
            <div className="cc-card">
              <div className="cc-qh"><span>Test budget per week (test markets only)</span></div>
              <BudgetInput value={s.budget} onChange={b => update({ budget: b })} ariaLabel="Test budget per week in thousands of dollars" />
              <span className="cc-help">The extra media spend for the test, on top of business as usual (BAU). Sent to {d.partner} as a limit for their power analysis.</span>
            </div>
            <ChangeField change={s.change} note={s.changeNote} onChange={p => update(p)} />
            <div className="cc-card">
              <span style={{ fontWeight: 600 }}>Guardrail rules for the plan</span>
              <ChecksList checks={forStage("plan")} compact />
            </div>
          </div>
          <div style={{ flex: "1 1 260px", maxWidth: 320, display: "flex", flexDirection: "column", gap: 12 }}>
            <ChecksSummary checks={forStage("plan")} />
            <div className="cc-card">
              <span style={{ fontWeight: 600, fontSize: 13.5 }}>Benchmarks</span>
              {library.length ? (
                <>
                  <div style={{ display: "flex", gap: 18 }}>
                    <div><div className="cc-lbl">Avg lift</div><div className="cc-val">+{avg(library.map(e => e.lift)).toFixed(1)}%</div></div>
                    <div><div className="cc-lbl">Met bar</div><div className="cc-val">{library.filter(e => e.met).length}/{library.length}</div></div>
                  </div>
                  <span className="cc-help">From {library.length} past test{library.length > 1 ? "s" : ""} in the Learning library.</span>
                </>
              ) : <span className="cc-help">No tests in the Learning library yet.</span>}
            </div>
            <button className={`cc-btn${planBlocks.length || !decisionText(s.decision) ? "" : " primary"}`} disabled={!!planBlocks.length || !decisionText(s.decision)} onClick={() => go(2)}>
              {!decisionText(s.decision) ? "Enter the decision to continue"
                : planBlocks.length ? `Clear ${planBlocks.length} blocker${planBlocks.length > 1 ? "s" : ""} to continue` : "Continue to Design →"}
            </button>
          </div>
        </div>
      )}

      {/* ── 2 · DESIGN ── */}
      {s.stage === 2 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 520px", minWidth: 0, display: "flex", flexDirection: "column", gap: 14 }}>
            <DeckUpload title={`Design deck from ${d.partner}`} kind="design" stored={d.designDeck} uploading={uploading.design || null}
              onPick={f => uploadDeck("design", f)} onDownload={() => download("design")} doneText="Stored" hint={`e.g. ${d.designFile}`} />
            {designStored && (
              <div className="cc-card">
                <div className="cc-qh"><span>Test design read from the deck</span><span className="cc-help">{d.designDeck?.name}{d.designParse?.pages ? ` · ${d.designParse.pages} pages` : ""}</span></div>
                {d.designParse?.error ? <div className="cc-note bad">{d.designParse.error}</div> : (
                  <>
                    <div className="cc-grid-3" style={{ gap: 10 }}>
                      <div className="cc-tile"><span className="cc-lbl">Method</span><span style={{ fontWeight: 600 }}>{d.methodText}</span></div>
                      <div className="cc-tile"><span className="cc-lbl">Flight</span><span className="cc-mono" style={{ fontWeight: 600 }}>{d.dz.weeks ? `${d.weeks} weeks` : "—"}</span></div>
                      <div className="cc-tile"><span className="cc-lbl">Budget</span><span className="cc-mono" style={{ fontWeight: 600 }}>{d.spendText}</span></div>
                      <div className="cc-tile"><span className="cc-lbl">Detectable lift (MDE)</span><span className="cc-mono" style={{ fontWeight: 600 }}>{d.mdeText}</span></div>
                      <div className="cc-tile"><span className="cc-lbl">Power</span><span className="cc-mono" style={{ fontWeight: 600 }}>{d.dz.power != null ? `${d.dz.power.toFixed(0)}%` : "—"}</span></div>
                      <div className="cc-tile"><span className="cc-lbl">Control</span><span style={{ fontWeight: 600 }}>{d.controlText}</span></div>
                    </div>
                    <div className="cc-tile"><span className="cc-lbl">Test markets{d.dz.n_test ? ` (${d.dz.n_test})` : ""}</span><span style={{ fontWeight: 600 }}>{d.marketsText}</span></div>
                    {!!d.designParse?.missing?.length && <div className="cc-note warn">Not found in the deck: {missingText(d.designParse.missing)}. Check that the deck states them (e.g. “Minimum detectable lift: 4.7%”) and upload it again.</div>}
                  </>
                )}
              </div>
            )}
          </div>
          <div style={{ flex: "1 1 280px", maxWidth: 320, display: "flex", flexDirection: "column", gap: 12 }}>
            <div className="cc-card">
              <span style={{ fontWeight: 600, fontSize: 13.5 }}>Design guardrails</span>
              <ChecksList checks={forStage("design")} compact />
            </div>
            <button className={`cc-btn${designNextOk ? " primary" : ""}`} disabled={!designNextOk} onClick={() => go(3)}>
              {!designStored ? "Upload the design deck to continue" : !designReadable ? "Upload a readable design deck to continue"
                : designBlocks.length ? `Clear ${designBlocks.length} blocker${designBlocks.length > 1 ? "s" : ""} to continue` : "Continue to Test plan →"}
            </button>
          </div>
        </div>
      )}

      {/* ── 3 · TEST PLAN ── */}
      {s.stage === 3 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 300px", maxWidth: 360, display: "flex", flexDirection: "column", gap: 12 }}>
            <div className="cc-card">
              <span style={{ fontWeight: 600 }}>Success bar</span>
              <span className="cc-fl">{rev ? "Success if iROAS is at least" : "Success if lift is at least"}</span>
              <div className="cc-stepper">
                <button aria-label="Lower" onClick={() => update(p => rev ? { bar: Math.max(0.5, p.bar - 0.5) } : { cntBar: Math.max(1, p.cntBar - 1) })}>−</button>
                <span>{rev ? s.bar.toFixed(1) + "x" : s.cntBar + "%"}</span>
                <button aria-label="Raise" onClick={() => update(p => rev ? { bar: Math.min(5, p.bar + 0.5) } : { cntBar: Math.min(15, p.cntBar + 1) })}>+</button>
              </div>
              <span className="cc-help">{rev ? "iROAS = incremental revenue ÷ ad spend. 2.0x means every $1 brought $2 of extra sales." : "Lift = how much higher the test markets were than expected without ads."}</span>
              <span className="cc-fl">If the bar is met</span>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className={pill(s.onWin === "scale")} onClick={() => update({ onWin: "scale" })}>Increase budget</button>
                <button className={pill(s.onWin === "roll")} onClick={() => update({ onWin: "roll" })}>Roll out to all markets</button>
                <button className={pill(s.onWin === "keep")} onClick={() => update({ onWin: "keep" })}>Keep current spend</button>
              </div>
            </div>
            <button className="cc-btn primary" onClick={() => go(4)}>Approve plan &amp; schedule →</button>
          </div>
          <article ref={planRef} className="cc-paper" style={{ flex: "999 1 440px", minWidth: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 10.5, letterSpacing: ".1em", color: "#6B7280", fontWeight: 700 }}>TEST PLAN · {d.advName.toUpperCase()}</span>
                <span style={{ fontSize: 12.5, color: "#6B7280" }}>{s.name.trim() || "Geo test"} · prepared {fmtDayYear(parseDay(todayIso()))}</span>
              </div>
              <button className="cc-btn cc-noprint" style={{ background: "#FFFFFF", color: "#374151", borderColor: "#D1D5DB" }} onClick={downloadPlan}>Download PDF</button>
            </div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>{question}</h2>
            <div style={{ border: "1.5px solid #2563EB", background: "#EFF6FF", borderRadius: 10, padding: "14px 16px", display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 14 }}>
              <div><div style={{ fontSize: 11.5, fontWeight: 700, color: "#047857" }}>IF {d.barCond}</div><div style={{ fontSize: 13.5, marginTop: 4 }}>{winTexts[s.onWin]}</div></div>
              <div><div style={{ fontSize: 11.5, fontWeight: 700, color: "#B45309" }}>IF NOT</div><div style={{ fontSize: 13.5, marginTop: 4 }}>{d.advName} keeps spend as is and retests with more markets or weeks.</div></div>
            </div>
            <div>
              <div className="cc-prow"><span style={{ color: "#6B7280" }}>Method</span><span>{d.methodText} · measured by {d.partnerCap}</span></div>
              <div className="cc-prow"><span style={{ color: "#6B7280" }}>KPI</span><span>{rev ? "Revenue ($), with iROAS" : "Count (no $ values)"}</span></div>
              <div className="cc-prow"><span style={{ color: "#6B7280" }}>Test markets</span><span>{d.dz.test_markets?.length ? `${d.dz.test_markets.length}: ${d.marketsText}` : d.marketsText}</span></div>
              <div className="cc-prow"><span style={{ color: "#6B7280" }}>Comparison</span><span>{d.controlText}</span></div>
              <div className="cc-prow"><span style={{ color: "#6B7280" }}>Based on</span><span>Design deck: {d.designDeck?.name || "—"}</span></div>
              <div className="cc-prow"><span style={{ color: "#6B7280" }}>Flight</span><span className="cc-mono">{d.flight}</span></div>
              <div className="cc-prow"><span style={{ color: "#6B7280" }}>Budget</span><span className="cc-mono">{d.spendLong}</span></div>
              <div className="cc-prow"><span style={{ color: "#6B7280" }}>Expected result</span><span>{d.dz.mde != null
                ? `Detects lifts of ${d.mdeText} or more${d.dz.power != null ? ` (power ${d.dz.power.toFixed(0)}%)` : ""}${d.dz.expected_incremental ? `; about ${rev ? dollars(d.dz.expected_incremental) : Math.round(d.dz.expected_incremental).toLocaleString()} incremental` : ""}`
                : "—"}</span></div>
              <div className="cc-prow" style={{ borderBottom: "1px solid #E5E7EB" }}><span style={{ color: "#6B7280" }}>Results</span><span>Read mid-flight: in-flight data goes to {d.partner} during the flight; {d.partner} measures lift{rev ? ", incremental revenue and iROAS" : " and cost per incremental"} so far and shares results decks as it goes, with the final one when the flight ends on {fmtDayYear(d.end)}.</span></div>
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.5 }}><b>Rules during the test:</b> ads run only in test markets; {s.change === "yes"
              ? `planned change in test markets: ${s.changeNote || "a promo, price change or other campaign"}`
              : "nothing else changes in test markets"}; in-flight data goes to {d.partner} for mid-flight reads through {fmtDayYear(d.end)}.</div>
            <div style={{ fontSize: 13, lineHeight: 1.5 }}><b>Accepted risks:</b> {
              [...forStage("plan"), ...forStage("design")].filter(c => c.status === "fail" && c.level === "guard").map(c => c.detail).join(" ") || "none."
            }</div>
          </article>
        </div>
      )}

      {/* ── 4 · IN FLIGHT: mid-flight results decks ── */}
      {s.stage === 4 && (
        <>
          <div className="cc-card">
            <div className="cc-tl">
              {phases.map((p, i) => (
                <div key={p[0]} className={`cc-tl-step${i < s.phase ? " done" : i === s.phase ? " cur" : ""}`}>
                  <span style={{ fontWeight: 600 }}>{p[0]}</span>
                  <span className="cc-mono" style={{ fontSize: 12 }}>{fmtDayYear(p[1])}</span>
                  <span className="cc-help">{p[2]}</span>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <button className={`cc-btn${s.phase >= 2 ? "" : " primary"}`} disabled={s.phase >= 2} onClick={() => update(p => ({ phase: Math.min(2, p.phase + 1) }))}>
                {["Jump to start date", "Jump to end date", "Flight ended"][Math.min(2, s.phase)]}
              </button>
              <span className="cc-help">Demo control: jump the calendar forward.</span>
            </div>
          </div>
          {s.phase < 1 ? (
            <div className="cc-note">{d.partner === "Adrsta" ? "Adrsta" : "The partner"} starts reading the test when the ads start on {fmtDayYear(d.start)}.</div>
          ) : (
            <>
          <DeckUpload title={s.phase >= 2 ? `Final results deck from ${d.partner}` : `Full-campaign results deck from ${d.partner}`} kind="results" stored={d.resultsDeck} uploading={uploading.results || null}
            onPick={f => uploadDeck("results", f)} onDownload={() => download("results")} doneText={d.final ? "Final read" : "Directional read"} hint={`e.g. ${d.resFile}`} />
          {resultsStored && d.resultsParse?.error && <div className="cc-note bad">{d.resultsParse.error}</div>}
          {resultsReadable && (
            <>
              {!!d.resultsParse?.missing?.length && <div className="cc-note warn">Not found in the deck: {missingText(d.resultsParse.missing)}. Check that the deck states them (e.g. “Confidence: 94%”) and upload it again.</div>}
              {!d.final && (
                <div className={`cc-note${d.tracking ? "" : " warn"}`}>
                  Directional read from {d.partner}: {d.tracking ? "on track for the success bar" : "behind the success bar"} so far
                  {d.conf != null ? `, confidence ${d.conf.toFixed(0)}%` : ""}. The result is decided on the final results deck, sent when the flight ends.
                </div>
              )}
              <div className="cc-grid-4">
                <div className="cc-card" style={{ gap: 4 }}><span className="cc-lbl">Lift so far</span><span className="cc-val" style={{ color: (d.lift ?? 0) >= 0 ? "var(--success-card)" : "var(--error)" }}>{d.liftText}</span><span className="cc-help">test vs {d.method === "RCT" ? "control markets" : "synthetic control"}</span></div>
                <div className="cc-card" style={{ gap: 4 }}><span className="cc-lbl">{rev ? "Incremental revenue" : "Incremental"}</span><span className="cc-val">{d.incText}</span><span className="cc-help">caused by the ads</span></div>
                <div className="cc-card" style={{ gap: 4 }}><span className="cc-lbl">{d.effLabel}</span><span className="cc-val">{d.effVal}</span><span className="cc-help">{rev ? (d.rz.spend_k != null ? `on ${money(d.rz.spend_k)} spend` : "spend not stated in the deck") : "spend ÷ extra conversions"}</span></div>
                <div className="cc-card" style={{ gap: 4 }}><span className="cc-lbl">Confidence</span><span className="cc-val" style={{ color: d.sig ? "var(--success-card)" : "var(--warning)" }}>{d.conf != null ? `${d.conf.toFixed(0)}%` : "—"}</span><span className="cc-help">{d.rz.pvalue != null ? `p-value ${d.rz.pvalue}` : "needs 80% or more"}</span></div>
              </div>
              {d.rz.series && d.rz.series.t.length > 1 && (
                <div className="cc-card">
                  <span style={{ fontWeight: 600 }}>{d.method === "RCT" ? "Test markets vs the prediction from control markets" : "Test markets vs their synthetic twin"}</span>
                  <ReadoutChart t={d.rz.series.t} c={d.rz.series.c} startIndex={d.rz.series.start_index} dates={d.rz.series.dates}
                    testLabel="Test markets" ctrlLabel={d.method === "RCT" ? "Predicted from control markets (no ads)" : "Synthetic twin (no ads)"} money={rev} />
                </div>
              )}
              {d.reads.length > 1 && (
                <div className="cc-card">
                  <span style={{ fontWeight: 600 }}>Results decks so far</span>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead><tr>{["Received", "Deck", "Lift", rev ? "iROAS" : "Incremental", "Confidence", "Read"].map(h => <th key={h} style={{ textAlign: "left", padding: "6px 8px", fontSize: 12, color: "var(--text-secondary)", borderBottom: "1px solid var(--border-subtle)" }}>{h}</th>)}</tr></thead>
                      <tbody>
                        {d.reads.map((r, i) => (
                          <tr key={`${r.uploaded_at}-${i}`}>
                            <td style={{ padding: "6px 8px" }} className="cc-mono">{(r.uploaded_at || "").slice(0, 10) || "—"}</td>
                            <td style={{ padding: "6px 8px" }}>{r.name}</td>
                            <td style={{ padding: "6px 8px" }} className="cc-mono">{r.lift != null ? `${r.lift >= 0 ? "+" : ""}${r.lift.toFixed(1)}%` : "—"}</td>
                            <td style={{ padding: "6px 8px" }} className="cc-mono">{rev ? (r.iroas != null ? `${r.iroas.toFixed(2)}x` : "—") : (r.incremental != null ? Math.round(r.incremental).toLocaleString() : "—")}</td>
                            <td style={{ padding: "6px 8px" }} className="cc-mono">{r.confidence != null ? `${r.confidence.toFixed(0)}%` : "—"}</td>
                            <td style={{ padding: "6px 8px" }}>{r.final ? "Final" : "Directional"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <div className="cc-card">
                <span style={{ fontWeight: 600, fontSize: 13.5 }}>Results guardrails</span>
                <ChecksList checks={forStage("results")} />
              </div>
            </>
          )}
            </>
          )}
          {/* Decide opens when the flight ends; a result that misses a rule (e.g. low confidence) is still recorded there. */}
          <button className={`cc-btn${s.phase >= 2 && d.hasResult ? " primary" : ""}`} style={{ alignSelf: "flex-start" }} disabled={s.phase < 2 || !d.hasResult} onClick={() => go(5)}>
            {s.phase < 2 ? `Decide opens when the flight ends (${fmtDayYear(d.end)})` : !d.hasResult ? "Upload a results deck to decide" : "Compare with the plan →"}
          </button>
        </>
      )}

      {/* ── 5 · DECIDE ── */}
      {s.stage === 5 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-start" }}>
          <article className="cc-paper" style={{ flex: "999 1 480px", minWidth: 0 }}>
            <span style={{ fontSize: 10.5, letterSpacing: ".1em", color: "#6B7280", fontWeight: 700 }}>RESULT VS PLAN</span>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>{question}</h2>
            <div style={{ borderRadius: 10, padding: "14px 16px", fontWeight: 600, ...(d.met ? { background: "#ECFDF5", border: "1.5px solid #047857", color: "#065F46" } : { background: "#FFFBEB", border: "1.5px solid #B45309", color: "#92400E" }) }}>
              {d.met ? "Yes: the success bar was met." : d.sig ? "No: there was a measurable effect, but it missed the bar." : `Not significant: confidence ${d.conf != null ? d.conf.toFixed(0) + "%" : "not stated"} is below 80%.`}
            </div>
            <div>
              <div className="cc-prow" style={{ gridTemplateColumns: "150px 1fr 1fr", fontWeight: 600, color: "#6B7280", fontSize: 12 }}><span>Metric</span><span>Planned</span><span>Actual</span></div>
              <div className="cc-prow" style={{ gridTemplateColumns: "150px 1fr 1fr" }}><span style={{ color: "#6B7280" }}>Success bar</span><span className="cc-mono">{d.barCond}</span><span className="cc-mono">{rev ? `iROAS ${d.iroas != null ? d.iroas.toFixed(2) + "x" : "—"}` : `lift ${d.liftText}`}{d.sig ? "" : " (not significant)"}</span></div>
              <div className="cc-prow" style={{ gridTemplateColumns: "150px 1fr 1fr" }}><span style={{ color: "#6B7280" }}>Lift</span><span className="cc-mono">≥ {d.mdeText} detectable</span><span className="cc-mono">{d.liftText}</span></div>
              <div className="cc-prow" style={{ gridTemplateColumns: "150px 1fr 1fr" }}><span style={{ color: "#6B7280" }}>Confidence</span><span className="cc-mono">80%</span><span className="cc-mono">{d.conf != null ? `${d.conf.toFixed(0)}%` : "—"}</span></div>
              <div className="cc-prow" style={{ gridTemplateColumns: "150px 1fr 1fr", borderBottom: "1px solid #E5E7EB" }}><span style={{ color: "#6B7280" }}>Spend</span><span className="cc-mono">{d.totK != null ? money(d.totK) : "—"}</span><span className="cc-mono">{d.spendKText}</span></div>
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.5, color: "#4B5563" }}>Applies to {d.flight}, these markets and this budget. Retest before applying it to another season or a national rollout. Numbers from {d.resultsDeck?.name || "the results deck"}.</div>
          </article>
          <div style={{ flex: "1 1 280px", maxWidth: 340, display: "flex", flexDirection: "column", gap: 12 }}>
            <div className="cc-card">
              <span style={{ fontWeight: 600 }}>Record the decision</span>
              <span className="cc-help">{d.met ? `The plan said: ${winTexts[s.onWin]}` : `Tip: ask ${d.partner} for a design with a higher weekly budget or more weeks.`}</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {decDefs.map(([key, label]) => (
                  <button key={key} className={`cc-combo${s.finalDecision === key ? " on" : ""}`} disabled={s.saved} onClick={() => update({ finalDecision: key })}>
                    <span className={`cc-radio${s.finalDecision === key ? " on" : ""}`} /><span style={{ flex: 1 }}>{label}</span>
                  </button>
                ))}
              </div>
              <button className={`cc-btn${s.saved ? " done" : s.finalDecision ? " primary" : ""}`} disabled={!s.finalDecision || s.saved}
                onClick={() => update(p => ({ saved: true, result: deckResult({ ...p, saved: true }, engine) }))}>
                {s.saved ? "Saved ✓" : "Save decision & close test"}
              </button>
            </div>
            {s.saved && <div className="cc-note ok">Saved. The test is closed and added to the Learning library as a benchmark, tagged “measured by {d.partner}”.</div>}
            {s.saved && <button className="cc-btn" onClick={() => navigate(`${BASE}/library`)}>Open Learning library →</button>}
            <button className="cc-btn" onClick={() => navigate(`${BASE}/new`)}>Run another new test</button>
          </div>
        </div>
      )}
    </div>
  );
}
